import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet,
  Clock,
  CheckCircle,
  AlertCircle,
  DollarSign,
  TrendingUp,
  Users,
  FileText,
  ClipboardCheck
} from 'lucide-react';
import { adminApi } from '../../api';
import { AdminStats } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { formatCurrency } from '../../utils/currency';

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await adminApi.getStats();
        if (response.success && response.data) {
          setStats(response.data.stats);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="admin-dashboard-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Admin Dashboard</h1>
          <p>Monitor platform performance, manage applications, and track loan portfolio</p>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {stats && (
        <>
          <div className="dashboard-section">
            <h2>Loan Portfolio</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <Wallet size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Loans</div>
                  <div className="stat-value">{stats.loans.total_loans}</div>
                </div>
              </div>

              <div className="stat-card stat-card-active">
                <div className="stat-icon">
                  <Clock size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Active Loans</div>
                  <div className="stat-value">{stats.loans.active_loans}</div>
                </div>
              </div>

              <div className="stat-card stat-card-success">
                <div className="stat-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Completed</div>
                  <div className="stat-value">{stats.loans.paid_loans}</div>
                </div>
              </div>

              <div className="stat-card stat-card-warning">
                <div className="stat-icon">
                  <AlertCircle size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Defaulted</div>
                  <div className="stat-value">{stats.loans.defaulted_loans}</div>
                </div>
              </div>

              <div className="stat-card highlight">
                <div className="stat-icon">
                  <DollarSign size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Disbursed</div>
                  <div className="stat-value stat-value-currency">
                    {formatCurrency(stats.loans.total_disbursed)}
                  </div>
                </div>
              </div>

              <div className="stat-card highlight">
                <div className="stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Outstanding Balance</div>
                  <div className="stat-value stat-value-currency">
                    {formatCurrency(stats.loans.total_outstanding)}
                  </div>
                </div>
              </div>

              <div className="stat-card highlight">
                <div className="stat-icon">
                  <ClipboardCheck size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Collected</div>
                  <div className="stat-value stat-value-currency">
                    {formatCurrency(stats.loans.total_collected)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-section">
            <h2>Applications</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <FileText size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Applications</div>
                  <div className="stat-value">{stats.applications.total_applications}</div>
                </div>
              </div>

              <div className="stat-card stat-card-warning">
                <div className="stat-icon">
                  <Clock size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Pending Review</div>
                  <div className="stat-value">{stats.applications.pending_applications}</div>
                </div>
              </div>

              <div className="stat-card stat-card-success">
                <div className="stat-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Approved</div>
                  <div className="stat-value">{stats.applications.approved_applications}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">
                  <AlertCircle size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Declined</div>
                  <div className="stat-value">{stats.applications.declined_applications}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-section">
            <h2>Customers</h2>
            <div className="stats-grid">
              <div className="stat-card stat-card-info">
                <div className="stat-icon">
                  <Users size={24} />
                </div>
                <div className="stat-content">
                  <div className="stat-label">Total Customers</div>
                  <div className="stat-value">{stats.customers.total_customers}</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <Link to="/admin/applications" className="action-card action-card-primary">
            <div className="action-icon">
              <FileText size={28} />
            </div>
            <div className="action-title">Review Applications</div>
            <div className="action-description">Process and approve pending loan requests</div>
          </Link>

          <Link to="/admin/loans" className="action-card">
            <div className="action-icon">
              <Wallet size={28} />
            </div>
            <div className="action-title">Manage Loans</div>
            <div className="action-description">Monitor active loans and repayments</div>
          </Link>

          <Link to="/admin/payments/pending" className="action-card">
            <div className="action-icon">
              <ClipboardCheck size={28} />
            </div>
            <div className="action-title">Verify Payments</div>
            <div className="action-description">Review pending payment verifications</div>
          </Link>

          <Link to="/admin/settings" className="action-card">
            <div className="action-icon">
              <TrendingUp size={28} />
            </div>
            <div className="action-title">Settings</div>
            <div className="action-description">Configure payment gateways, NIBSS API, and system settings</div>
          </Link>
        </div>
      </div>
    </div>
  );
};
